/**
 * 真 CIDv1（raw codec + sha2-256 + base32）—— 修复「假 CID」隐患。
 *
 * 字节布局：`0x01`(CIDv1) `0x55`(raw) `0x12`(sha2-256) `0x20`(len 32) ‖ digest(32B)
 * 字符串：`'b' + base32(bytes)`（RFC4648 小写字母表，无 padding）
 *
 * 这样生成的 CID 与未来 Helia `addBytes({ rawLeaves: true, cidVersion: 1 })` 完全一致（ARCH §S15）。
 */

import { hashSha256 } from './noble';
import { MeshLinkError } from '@/lib/result';

const BASE32_ALPHABET = 'abcdefghijklmnopqrstuvwxyz234567';
const CID_V1 = 0x01;
const CODEC_RAW = 0x55;
const HASH_SHA2_256 = 0x12;
const DIGEST_LENGTH = 0x20;

/** 组装 CIDv1 原始字节（不含 base32 编码）。 */
export function cidBytes(digest: Uint8Array): Uint8Array {
  if (digest.length !== 32) throw new MeshLinkError('E_INVALID_INPUT', 'sha2-256 digest 必须是 32 字节');
  const out = new Uint8Array(4 + digest.length);
  out[0] = CID_V1;
  out[1] = CODEC_RAW;
  out[2] = HASH_SHA2_256;
  out[3] = DIGEST_LENGTH;
  out.set(digest, 4);
  return out;
}

/** base32 编码（RFC4648，小写，无 padding）。 */
export function base32Encode(bytes: Uint8Array): string {
  const alphabet = BASE32_ALPHABET;
  let bits = 0;
  let value = 0;
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      out += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += alphabet[(value << (5 - bits)) & 31];
  return out;
}

/** base32 解码（小写字母表）。非法字符返回 null。 */
export function base32Decode(input: string): Uint8Array | null {
  const clean = input.trim().toLowerCase().replace(/=+$/, '');
  const lookup = new Map<string, number>();
  for (let i = 0; i < BASE32_ALPHABET.length; i += 1) lookup.set(BASE32_ALPHABET[i], i);

  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of clean) {
    const index = lookup.get(ch);
    if (index === undefined) return null;
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

/** 计算内容的真 CIDv1。 */
export function computeCid(bytes: Uint8Array): string {
  const digest = hashSha256(bytes);
  return 'b' + base32Encode(cidBytes(digest));
}

/** 校验字符串是否形如真 CIDv1（raw + sha2-256 → 固定 59 字符）。 */
export function isCidV1(value: string): boolean {
  return typeof value === 'string' && /^b[a-z2-7]{58}$/.test(value);
}

/** 从 CID 反解出 sha256 digest（用于校验内容完整性）。失败返回 null。 */
export function cidToDigest(cid: string): Uint8Array | null {
  if (!isCidV1(cid)) return null;
  const bytes = base32Decode(cid.slice(1));
  if (!bytes || bytes.length !== 36) return null;
  if (bytes[0] !== CID_V1 || bytes[1] !== CODEC_RAW || bytes[2] !== HASH_SHA2_256 || bytes[3] !== DIGEST_LENGTH) {
    return null;
  }
  return bytes.slice(4);
}

/** 校验给定字节是否与该 CID 对应。 */
export function verifyCid(cid: string, bytes: Uint8Array): boolean {
  const digest = cidToDigest(cid);
  if (!digest) return false;
  const actual = hashSha256(bytes);
  if (actual.length !== digest.length) return false;
  let diff = 0;
  for (let i = 0; i < actual.length; i += 1) diff |= actual[i] ^ digest[i];
  return diff === 0;
}
